import { InlineBanner } from '@/components/commons/inline-banner'
import { Button } from '@/components/ui/button'
import { AlertTriangleFillIcon } from '@/components/ui/icons'
import { useTranslation } from '@/translations/i18n'

interface IncompleteAccountsBannerProps {
	/**
	 * How many non-walk-in rows carry no Salesforce account at all
	 * ([[incompleteAccountRows]]). Renders nothing at zero.
	 */
	count: number
	/**
	 * Narrows the roster below to exactly these rows — the fix surface for what
	 * this warns about. The filtering is client-side, so it costs no request.
	 */
	onReview: () => void
}

/**
 * Roster completeness warning: these attendees hold neither a child nor a parent
 * Salesforce account, so they will not appear in the export CSV at all.
 *
 * The backend raises the same rule as its M8 completeness gate on POST /lock; here
 * it is drawn in every state, on both the attendees tab and the check-in desk, so
 * the rows do not fail silently at the end of the event, when the CSV is the only
 * thing left to produce. Never a blocker: it states what to fix next rather than
 * asking for an acknowledgement.
 *
 * The count is recomputed from the roster rather than read off the lock response,
 * which carries it once and is gone on the next refresh. See
 * [[incompleteAccountRows]] for why that is exact.
 */
export function IncompleteAccountsBanner({
	count,
	onReview
}: IncompleteAccountsBannerProps) {
	const { t } = useTranslation()

	if (count <= 0) {
		return null
	}

	return (
		<InlineBanner
			action={
				<Button
					// White on the banner's grey, as the design draws it: no
					// outline, the fill alone lifts it off the block.
					className='l:w-auto w-full bg-neutral-white'
					onClick={onReview}
					type='button'
					variant='tertiary'
				>
					{t('marquee:incomplete_accounts_banner_action')}
				</Button>
			}
			description={t('marquee:incomplete_accounts_banner_description', {
				count
			})}
			icon={<AlertTriangleFillIcon className='size-5 text-orange-600' />}
			iconPlacement='title'
			title={t('marquee:incomplete_accounts_banner_title', { count })}
		/>
	)
}
